import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api.js";
import type { EventStatus } from "@mma/shared";
import { EventStatusBadge } from "../components/EventStatusBadge.js";
import { DeployStatusBadge } from "../components/DeployStatusBadge.js";

interface EventRow {
  id: string;
  name: string;
  date: string;
  location: string | null;
  status: EventStatus;
  _count: { fights: number };
}

export function AdminEventsPage() {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", date: "", location: "" });

  async function load() {
    setEvents(await api.get<EventRow[]>("/api/events"));
  }
  useEffect(() => {
    load();
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name || !form.date) return;
    await api.post("/api/events", {
      name: form.name,
      date: new Date(form.date).toISOString(),
      location: form.location || null,
    });
    setForm({ name: "", date: "", location: "" });
    setCreating(false);
    load();
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Admin — Events</h1>
        <Link to="/" className="text-sm text-neutral-400 hover:text-neutral-200">
          ← Forside
        </Link>
      </header>

      <DeployStatusBadge />

      <div className="flex gap-2">
        <button
          onClick={() => setCreating(!creating)}
          className="tap-btn px-4 py-2 bg-blue-600 hover:bg-blue-500 text-sm"
        >
          + Nyt event
        </button>
        <Link
          to="/admin/import"
          className="tap-btn px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-sm"
        >
          Importér fra ark
        </Link>
        <Link
          to="/admin/fighters"
          className="tap-btn px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-sm ml-auto"
        >
          Fightere
        </Link>
        <Link
          to="/admin/gfx"
          className="tap-btn px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-sm"
        >
          Grafik
        </Link>
      </div>

      {creating && (
        <form onSubmit={create} className="bg-neutral-900 p-4 rounded-xl space-y-2">
          <input
            placeholder="Galla-navn"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-neutral-800 rounded px-3 py-2"
          />
          <input
            type="datetime-local"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="w-full bg-neutral-800 rounded px-3 py-2"
          />
          <input
            placeholder="Lokation"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            className="w-full bg-neutral-800 rounded px-3 py-2"
          />
          <button type="submit" className="tap-btn px-4 py-2 bg-emerald-600">
            Opret
          </button>
        </form>
      )}

      <div className="space-y-2">
        {events.map((e) => (
          <Link
            key={e.id}
            to={`/admin/events/${e.id}`}
            className="block bg-neutral-900 hover:bg-neutral-800 rounded-xl p-4 border border-neutral-800"
          >
            <div className="flex justify-between items-baseline">
              <span className="font-semibold">{e.name}</span>
              <EventStatusBadge status={e.status} />
            </div>
            <div className="text-sm text-neutral-400">
              {new Date(e.date).toLocaleString("da-DK")}
              {e.location && ` · ${e.location}`}
              {` · ${e._count.fights} kampe`}
            </div>
          </Link>
        ))}
        {events.length === 0 && (
          <div className="text-neutral-500 text-sm">Ingen events endnu.</div>
        )}
      </div>
    </div>
  );
}
